import { AllowedMenuItemCodes, IMenuData } from "@multiweb/types/contracts";
import { SerializableKeyEvent, SerializableMouseEvent } from "@multiweb/types/react";
import {
    ADD_PROFILE_TO_RELATIONSHIPS,
    CLEAR_LAST_SUBMENU,
    CLOSE_SUBMENUS,
    CONCAT_NOTIFICATION_ITEMS,
    CONCAT_PANEL_ITEMS,
    INCREASE_NOTIFICATIONS_PAGE_COUNT,
    LOAD_NEXT_NOTIFICATION_ITEMS_PAGE,
    MENU_ITEM_ACTIVATED,
    MENU_ITEM_DEFAULT_ACTION_ALLOWED,
    RELOAD_MENU,
    REMOVE_PROFILE_FORM_RELATIONSHIPS,
    REMOVE_PROFILE_FROM_FORMS,
    RESET_NOTIFICATION_ITEMS,
    RESET_NOTIFICATIONS_PAGE_COUNT,
    SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ,
    SET_ACTIVE_SECTION,
    SET_ACTIVE_SUBMENU,
    SET_ALLOWED_MENU_ITEM_CODES,
    SET_FORM_ITEM_ACTION_RESTRICTIONS,
    SET_MENU_FLOAT_TRANSITION_ACTIVE,
    SET_MENU_ITEMS,
    SET_MENU_PANEL_CAPTIONS_VISIBILITY,
    SET_MENU_VISIBILITY,
    SET_NOTIFICATION_GROUPS,
    SET_NOTIFICATION_GROUPS_PANEL_ITEMS,
    SET_PROFILE_ACTION_RESTRICTIONS,
    SET_VISUAL_AUTOMAT_STATE,
    SHOW_GROUP_NOTIFICATIONS,
    TOGGLE_SUBMENU,
    VISUAL_AUTOMAT_PROCESSED,
} from "./constants";
import { NavigationSection } from "./navigation";
import {
    FlatNotificationGroups,
    IAddProfileToRelationshipsAction,
    IClearLastSubmenuAction,
    ICloseSubmenusAction,
    IConcatNotificationItemsAction,
    IConcatPanelItemsAction,
    IIncreaseNotificationsPageCountAction,
    ILoadNextNotificationItemsPageAction,
    IMenuItemActivatedAction,
    IMenuItemDefaultActionAllowedAction,
    IReloadMenuAction,
    IRemoveProfileFormRelationshipsAction,
    IRemoveProfileFromFormsAction,
    IResetNotificationItemsAction,
    IResetNotificationsPageCountAction,
    ISetActiveGroupNotificationsAsReadAction,
    ISetActiveSectionAction,
    ISetActiveSubmenuAction,
    ISetAllowedMenuItemCodesAction,
    ISetFormItemActionRestrictionsAction,
    ISetMenuFloatTransitionActive,
    ISetMenuItemsAction,
    ISetMenuPanelCaptionsVisibilityAction,
    ISetMenuVisibilityAction,
    ISetNotificationGroupsAction,
    ISetNotificationGroupsPanelItemsAction,
    ISetProfileActionRestrictionsAction,
    ISetVisualAutomatStateAction,
    IShowGroupNotificationsAction,
    IToggleSubmenuAction,
    IVisualAutomatProcessedAction,
    MenuItemActivatedType,
    MenuPanelItems,
    MenuVisibility,
    NotificationItems,
    SectionId,
    VisualAutomatState,
} from "./types";

/**
 * Nastaví položky menu do stavu.
 *
 * @param menuItems Data položek menu.
 */
export const createSetMenuItemsAction = (menuItems: IMenuData): ISetMenuItemsAction => ({
    type: SET_MENU_ITEMS,
    payload: {
        menuItems,
    },
});

/**
 * Nastaví viditelnost menu.
 * @param menuVisibility Viditelnost menu
 */
export const createSetMenuVisibilityAction = (menuVisibility: MenuVisibility): ISetMenuVisibilityAction => ({
    type: SET_MENU_VISIBILITY,
    payload: {
        menuVisibility,
    },
});

/**
 * Nastaví submenu se zadaným id jako nejzanořenější aktivní submenu (všichni jeho předci jsou automaticky aktivní).
 * @param submenuId
 * @param sectionId Id položky na hlavním panelu, v rámci které má být aktivováno dané podmenu.
 */
export const createSetActiveSubmenuAction = (submenuId: number, sectionId: SectionId): ISetActiveSubmenuAction => ({
    type: SET_ACTIVE_SUBMENU,
    payload: {
        submenuId,
        sectionId,
    },
});

/**
 * Zavře všechna aktivní submenu.
 */
export const createCloseSubmenusAction = (): ICloseSubmenusAction => ({
    type: CLOSE_SUBMENUS,
});

/**
 * Vymaže z reduxu informaci o naposledy otevřeném podmenu v sekci `sectionId`.
 * @param sectionId Id položky na hlavním panelu, v rámce které má být informace o `lastSubmenuId` vymazána.
 */
export const createClearLastSubmenuAction = (sectionId: SectionId): IClearLastSubmenuAction => ({
    type: CLEAR_LAST_SUBMENU,
    payload: {
        sectionId,
    },
});

/**
 * Nastaví zadanou sekci v levém panelu menu jako aktivní.
 * @param activeSectionId Id sekce, která se stane aktivní.
 */
export const createSetActiveSectionAction = (activeSectionId: SectionId): ISetActiveSectionAction => ({
    type: SET_ACTIVE_SECTION,
    payload: {
        activeSectionId,
    },
});

/**
 * Akce reprezentující interakci uživatele se submenu.
 * @param submenuId
 */
export const createToggleSubmenuAction = (submenuId: number): IToggleSubmenuAction => ({
    type: TOGGLE_SUBMENU,
    payload: {
        submenuId,
    },
});

/**
 * Akce pro aktualizaci aliasů povolených kořenových uzlů hlavního navigačního menu.
 *
 * @param allowedMenuItemCodes Aliasy povolených kořenových uzlů.
 */
export const createSetAllowedMenuItemCodesAction = (allowedMenuItemCodes: AllowedMenuItemCodes): ISetAllowedMenuItemCodesAction => ({
    type: SET_ALLOWED_MENU_ITEM_CODES,
    payload: {
        allowedMenuItemCodes,
    },
});

/**
 * Akce pro rozšíření stavu notifikačních položek o další položky.
 * @param notificationItems Notifikační položky
 */
export const createConcatNotificationItemsAction = (notificationItems: NotificationItems): IConcatNotificationItemsAction => ({
    type: CONCAT_NOTIFICATION_ITEMS,
    payload: {
        notificationItems,
    },
});

/**
 * Akce pro vyprázdnění stavu notifikačních položek.
 */
export const createResetNotificationItemsAction = (): IResetNotificationItemsAction => ({
    type: RESET_NOTIFICATION_ITEMS,
});

/**
 * Akce pro uložení notifikačních skupin do stavu.
 */
export const createSetNotificationGroupsAction = (notificationGroups: FlatNotificationGroups): ISetNotificationGroupsAction => ({
    type: SET_NOTIFICATION_GROUPS,
    payload: {
        notificationGroups,
    },
});

/**
 * Akce pro požadavek na načtení další stránky notifikačních položek.
 */
export const createLoadNextNotificationItemsPageAction = (): ILoadNextNotificationItemsPageAction => ({
    type: LOAD_NEXT_NOTIFICATION_ITEMS_PAGE,
});

/**
 * Akce pro zvýšení počtu načtených stránek notifikačních položek.
 */
export const createIncreaseNotificationsPageCountAction = (): IIncreaseNotificationsPageCountAction => ({
    type: INCREASE_NOTIFICATIONS_PAGE_COUNT,
});

/**
 * Akce pro resetování počtu načtených stránek notifikačních položek.
 */
export const createResetNotificationsPageCountAction = (): IResetNotificationsPageCountAction => ({
    type: RESET_NOTIFICATIONS_PAGE_COUNT,
});

/**
 * Akce pro zobrazení formuláře s přehledem všech notifikací aktivní notifikační skupiny.
 */
export const createShowGroupNotificationsAction = (): IShowGroupNotificationsAction => ({
    type: SHOW_GROUP_NOTIFICATIONS,
});

/**
 * Akce pro označení všech notifikací aktivní notifikační skupiny jako přečtené.
 */
export const createSetActiveGroupNotificationsAsReadedAction = (): ISetActiveGroupNotificationsAsReadAction => ({
    type: SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ,
});

/**
 * Akce pro nastavení omezení pro zobrazení akčních položek, které otevírají novou instanci formulářové položky.
 * @param formItemId Id formulářové položky, pro kterou se nastaví omezení.
 * @param hideWindowActions True zapne omezení akcí, false vypne.
 */
export const createSetFormItemActionRestrictionsAction = (formItemId: number, hideWindowActions: boolean): ISetFormItemActionRestrictionsAction => ({
    type: SET_FORM_ITEM_ACTION_RESTRICTIONS,
    payload: {
        formItemId,
        hideWindowActions,
    },
});

/**
 * Akce pro nastavení omezení pro zobrazení akčních položek, které otevírají novou instanci profilové položky.
 * @param profileId Id profilové položky, pro kterou se nastaví omezení.
 * @param hideWindowActions True zapne omezení akcí, false vypne.
 */
export const createSetProfileActionRestrictionsAction = (profileId: number, hideWindowActions: boolean): ISetProfileActionRestrictionsAction => ({
    type: SET_PROFILE_ACTION_RESTRICTIONS,
    payload: {
        profileId,
        hideWindowActions,
    },
});

/**
 * Akce pro rozšíření stavu položek panelu menu
 * @param panelItems
 */
export const createConcatPanelItemsAction = (panelItems: MenuPanelItems): IConcatPanelItemsAction => ({
    type: CONCAT_PANEL_ITEMS,
    payload: {
        panelItems,
    },
});

/**
 * Akce pro nahrazení položek notifikačních skupin pro panel menu. Původní položky notifikačních skupin jsou zahozeny.
 * Položky jiných typů než {@link MenuPanelItemType.notificationGroup} nejsou ovlivněny. Nové položky se řadí na konec.
 * Pokud ve stavu nebyly žádné notifikační skupiny, chová se stejně jako {@link createConcatPanelItemsAction}.
 * @param notificationGroupsPanelItems Položky nových notifikačních skupin.
 */
export const createSetNotificationGroupsPanelItemsAction = (notificationGroupsPanelItems: MenuPanelItems): ISetNotificationGroupsPanelItemsAction => ({
    type: SET_NOTIFICATION_GROUPS_PANEL_ITEMS,
    payload: {
        notificationGroupsPanelItems,
    },
});

/**
 * Akce pro nastavení příznaku pro zobrazení popisků v menu panelu.
 * @param visible Příznak, který se má nastavit.
 */
export const createSetMenuPanelCaptionsVisibilityAction = (visible: boolean): ISetMenuPanelCaptionsVisibilityAction => ({
    type: SET_MENU_PANEL_CAPTIONS_VISIBILITY,
    payload: {
        visible,
    },
});

/**
 * Akce indikující aktivaci položky menu.
 * @param itemId Identifikátor položky unikátní v rámci svého typu.
 * @param itemType Typ položky.
 * @param section Sekce, ve které je aktivovaná položka vykreslená.
 * @param mouseEvent Událost kliknutí myši, pokud byla aktivace vyvolána myší.
 * @param keyEvent Událost stisku klávesy, pokud byla událost vyvolána klávesnicí.
 */
export const createMenuItemActivatedAction = (itemId: string | number, itemType: MenuItemActivatedType, section: NavigationSection, mouseEvent?: SerializableMouseEvent, keyEvent?: SerializableKeyEvent, preventFormDefault?: boolean, profileId?: number): IMenuItemActivatedAction => ({
    type: MENU_ITEM_ACTIVATED,
    payload: {
        itemId,
        itemType,
        profileId,
        section,
        mouseEvent,
        keyEvent,
        preventFormDefault,
    },
});

/**
 * Akce indikující povolení výchozího chování po aktivaci položky menu. Akce je nezbytná kvůli možnosti potlačit výchozí chování.
 * @param menuItemActivatedAction Akce aktivace položky menu.
 */
export const createMenuItemDefaultActionAllowedAction = (menuItemActivatedAction: IMenuItemActivatedAction): IMenuItemDefaultActionAllowedAction => ({
    type: MENU_ITEM_DEFAULT_ACTION_ALLOWED,
    payload: menuItemActivatedAction.payload,
});

/**
 * Akce indikující dokončení zpracování vizuálního automatu pro viditelnost menu.
 */
export const createVisualAutomatProcessedAction = (): IVisualAutomatProcessedAction => ({
    type: VISUAL_AUTOMAT_PROCESSED,
});

/**
 * Nastaví požadovaný stav ve vizuálním automatu.
 * @param state
 */
export const createSetVisualAutomatStateAction = (state: VisualAutomatState): ISetVisualAutomatStateAction => ({
    type: SET_VISUAL_AUTOMAT_STATE,
    payload: {
        state,
    },
});

/**
 * Akce nastavující příznak, zda je aktivní float přechod menu
 * @param menuFloatTransitionActive Příznak, zda je aktivní float přechod menu
 */
export const createSetMenuFloatTransitionActiveAction = (menuFloatTransitionActive: boolean): ISetMenuFloatTransitionActive => ({
    type: SET_MENU_FLOAT_TRANSITION_ACTIVE,
    payload: {
        menuFloatTransitionActive,
    },
});

/**
 * Akce pro smazání profilu odkazovaného v položkách formulářů. Reducer neprovádí vyhledávání, ale dostane rovnou id formulářových položek,
 * protože toto hledání je nutné provést v sáze kvůli aktualizaci dalších souvisejících stavů.
 * @param profileId Id profilu, který byl smazán.
 * @param formItemIds Ids formulářových položek, které se na tento profil odkazují.
 */
export const createRemoveProfileFromFormsAction = (profileId: number, formItemIds: number[]): IRemoveProfileFromFormsAction => ({
    type: REMOVE_PROFILE_FROM_FORMS,
    payload: {
        profileId,
        formItemIds,
    },
});

/**
 * Akce pro odstranění položek definujících vazby mezi profily a formuláři.
 * @param ids Id položek k odstranění.
 */
export const createRemoveProfileFormRelationshipsAction = (ids: string[]): IRemoveProfileFormRelationshipsAction => ({
    type: REMOVE_PROFILE_FORM_RELATIONSHIPS,
    payload: {
        ids,
    },
});

/**
 * Přidá profil se zadaným id do stavu definujícího vazby mezi profily a položkami formulářů. Profil je přidán jako samostatná položka, není napojen
 * na žádnou položku formuláře.
 * @param profileId
 */
export const createAddProfileToRelationshipsAction = (profileId: number): IAddProfileToRelationshipsAction => ({
    type: ADD_PROFILE_TO_RELATIONSHIPS,
    payload: {
        profileId,
    },
});

/**
 * Akce pro reload menu. Na začátku akce se zobrazí indikátor značící procesování akce a následně se přenačte historie položek menu, položky menu, lokalizace pro statické položky menu a položky notifikací.
 * Po ukončení procesování indikátor zmizí.
 */
export const createReloadMenuAction = (): IReloadMenuAction => ({
    type: RELOAD_MENU,
});
